// The "?" note, with an optional UManitoba email field. Used twice: once on the
// user's first visit (opened by PathfinderTab, with the welcome copy) and again
// from the "?" on the search box. Only the copy differs, via the title/blurb
// props. The email (if entered) is sent via onEmail and stored on the separate
// email list.
//
// The "?" is now the ONLY button in that corner — the "!" report button beside
// it is gone — so this popup carries the whole answer to "who made this and how
// do I tell them something": one address for reports, questions and feedback
// alike. A person who wants to report a wrong room and a person who wants to
// say thanks were never going to sort themselves into two buttons correctly,
// and a wrong route is easier to describe in a sentence of their own than in
// any form we could have written.
// Copy-out clean: no imports outside src/pathfinder.

import { useState } from 'react';
import InstagramButton from './InstagramButton.jsx';
import DonateButton from './DonateButton.jsx';

const ALLOWED = /^[^\s@]+@(myumanitoba\.ca|umanitoba\.ca)$/i;

const CONTACT = 'umapscontact@gmail.com';
const DEFAULT_TITLE = 'About UMaps';
const DEFAULT_BLURB =
  'UMaps is a student-built app, if you have any reports, questions, or ' +
  `feedback please reach out at ${CONTACT}!`;
// Kept as its own line rather than folded into the blurb: it is the one
// sentence here that is a claim about who we are NOT, and it has to survive
// somebody rewriting the friendly half above it.
const DISCLAIMER =
  'UMaps is student created and not associated with the University of Manitoba.';

/**
 * The blurb, with the things in it you can actually go to made tappable: our
 * contact address, and any link the copy names.
 *
 * They stay INSIDE the sentence — repeating an address as a separate button
 * would print it twice in a small box — but on a phone, text in a modal cannot
 * be selected and copied, so an address that is only text is an address nobody
 * can use. That applies just as much to a donation link written out in the
 * middle of a paragraph, which is why this is no longer only about mailto.
 *
 * Done by splitting rather than by hard-coding the markup, so each piece of
 * copy stays one readable string in one place.
 */
const LINKABLE = new RegExp(`(https?://[^\\s,)]+|${CONTACT})`, 'gi');

function linkify(text) {
  if (typeof text !== 'string') return text;
  return String(text).split(LINKABLE).map((part, i) => {
    if (part?.toLowerCase() === CONTACT) {
      return <a key={i} className="pfa-fb-mail" href={`mailto:${CONTACT}`}>{part}</a>;
    }
    if (/^https?:\/\//i.test(part || '')) {
      return (
        <a key={i} className="pfa-fb-mail" href={part} target="_blank" rel="noopener noreferrer">
          {/* The scheme is noise in a sentence somebody is reading, not typing. */}
          {part.replace(/^https?:\/\//i, '')}
        </a>
      );
    }
    return part;
  });
}

export default function SupportPopup({
  onEmail, onClose, title = DEFAULT_TITLE, blurb = DEFAULT_BLURB, disclaimer = DISCLAIMER,
  // What the email box is for. The "?" blurb no longer explains it — it is
  // about how to reach us — so the field says so itself.
  emailPrompt = 'Or leave your UManitoba email for updates:',
  // Where to donate, from the host. Present → a Buy Me a Coffee button in the
  // actions row; absent → the row is whatever else is in it.
  donateHref = '',
}) {
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = () => {
    const v = email.trim();
    if (!ALLOWED.test(v)) { setErr('Please use your @myumanitoba.ca or @umanitoba.ca email.'); return; }
    onEmail?.(v.toLowerCase());
    setDone(true);
  };

  return (
    <div className="pfa-fb-backdrop" onClick={onClose}>
      <div className="pfa-fb-card" onClick={e => e.stopPropagation()}>
        <button className="pfa-fb-close" onClick={onClose} aria-label="Dismiss">✕</button>
        <div className="pfa-fb-title">{title}</div>
        <div className="pfa-fb-sub">{linkify(blurb)}</div>
        {disclaimer && <div className="pfa-fb-disclaimer">{disclaimer}</div>}
        {done ? (
          <div className="pfa-fb-thanks">Thanks for your support!</div>
        ) : (
          <>
            {/* ⚠️ NO COLLECTOR, NO BOX. The popup is used for two different
                asks now, and the donation one has no business showing an email
                field: a personal appeal with a capture form under it reads as
                the appeal being the bait. `onEmail` is the host saying it
                wants addresses, so it is also the honest test for whether to
                ask for one. */}
            {onEmail && (
              <>
                {emailPrompt && <div className="pfa-fb-prompt">{emailPrompt}</div>}
                <input
                  className="pfa-fb-other"
                  type="email"
                  autoFocus
                  maxLength={160}
                  autoComplete="email"
                  placeholder="you@myumanitoba.ca"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (err) setErr(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                />
                {err && <div className="pfa-fb-err">{err}</div>}
              </>
            )}
            <div className="pfa-fb-actions">
              <InstagramButton />
              <DonateButton href={donateHref} />
              {onEmail && (
                <button className="pfa-fb-submit" disabled={!email.trim()} onClick={submit}>Submit</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
